import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { I18nProvider, useI18n } from '@/contexts/I18nContext';
import { PlanProvider } from '@/contexts/PlanContext';
import { PaywallProvider } from '@/contexts/PaywallContext';
import { Toaster } from 'sonner';
import { ConfirmDialogHost } from '@/components/ConfirmDialog';
import './App.css';

// Critical path - loaded eagerly (login, dashboard, layout)
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import AcceptInvite from '@/pages/auth/AcceptInvite';
import AuthCallback from '@/pages/auth/AuthCallback';
import Dashboard from '@/pages/Dashboard';
import Welcome from '@/pages/Welcome';
import LandingPage from '@/pages/LandingPage';
import Layout from '@/components/Layout';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import BetaGate from '@/components/BetaGate';
import { PageLoader } from '@/components/Skeletons';

// Lazy-loaded pages (non-critical)
const TimeTracking = lazy(() => import('@/pages/TimeTracking'));
const ReceiptsList = lazy(() => import('@/pages/receipts/ReceiptsList'));
const ReceiptUpload = lazy(() => import('@/pages/receipts/ReceiptUpload'));
const ReceiptDetail = lazy(() => import('@/pages/receipts/ReceiptDetail'));
const Projects = lazy(() => import('@/pages/Projects'));
const ProjectAukavinna = lazy(() => import('@/pages/project/ProjectAukavinna'));
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail'));
const Reports = lazy(() => import('@/pages/Reports'));
const Categories = lazy(() => import('@/pages/Categories'));
const HoursOverview = lazy(() => import('@/pages/HoursOverview'));
const CompanySettings = lazy(() => import('@/pages/CompanySettings'));
const SupplierOverrides = lazy(() => import('@/pages/SupplierOverrides'));
const AccountSettingsPage = lazy(() => import('@/pages/AccountSettingsPage'));
const AccountingSettings = lazy(() => import('@/pages/AccountingSettings'));
const Instructions = lazy(() => import('@/pages/Instructions'));
const BillingSettings = lazy(() => import('@/pages/BillingKontoSettings'));
const BillingKontoSettings = lazy(() => import('@/pages/BillingKontoSettings'));
const IntegrationsSettings = lazy(() => import('@/pages/IntegrationsSettings'));
const BillingDashboard = lazy(() => import('@/pages/BillingDashboard'));
// WidgetTest removed 2026-07 alongside Repeat.is
const PlatformShell = lazy(() => import('@/components/platform/PlatformShell'));
const PlatformDashboard = lazy(() => import('@/pages/platform/PlatformDashboard'));
const PlatformCompanyDetail = lazy(() => import('@/pages/platform/CompanyDetail'));
const PlatformAuditLogs = lazy(() => import('@/pages/platform/AuditLogs'));
const PlatformInviteCodes = lazy(() => import('@/pages/platform/InviteCodes'));
const PlatformFeedback = lazy(() => import('@/pages/platform/PlatformFeedback'));
const PlatformWaitlist = lazy(() => import('@/pages/platform/PlatformWaitlist'));
const PlatformWebhookAudit = lazy(() => import('@/pages/platform/WebhookAudit'));
const PlatformBillingReconciliation = lazy(() => import('@/pages/platform/BillingReconciliation'));
const PlatformBillingDuplicates = lazy(() => import('@/pages/platform/BillingDuplicates'));
const PlatformSyncHealth = lazy(() => import('@/pages/platform/SyncHealth'));
const PlatformFeatureInventory = lazy(() => import('@/pages/platform/FeatureInventory'));
const PlatformSpineTestWalkthrough = lazy(() => import('@/pages/platform/SpineTestWalkthrough'));
const PlatformSpineTestBugs = lazy(() => import('@/pages/platform/SpineTestBugs'));
const PlatformSpineTestLog = lazy(() => import('@/pages/platform/SpineTestLog'));
const Feedback = lazy(() => import('@/pages/Feedback'));
const PrivacyPolicy = lazy(() => import('@/pages/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/legal/TermsOfService'));
const ContactPage = lazy(() => import('@/pages/legal/ContactPage'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const VerdPricing = lazy(() => import('@/pages/VerdPricing'));
const ExampleProject = lazy(() => import('@/pages/ExampleProject'));
const PaywallPage = lazy(() => import('@/pages/PaywallPage'));
const FinancialOverview = lazy(() => import('@/pages/FinancialOverview'));
const UnitPrices = lazy(() => import('@/pages/UnitPrices'));
const Quotes = lazy(() => import('@/pages/Quotes'));
const Tilbodsgerd = lazy(() => import('@/pages/Tilbodsgerd'));
const QuoteEditor = lazy(() => import('@/pages/QuoteEditor'));
const QuotePublic = lazy(() => import('@/pages/QuotePublic'));
const Customers = lazy(() => import('@/pages/Customers'));
const CustomerDetail = lazy(() => import('@/pages/CustomerDetail'));

import ChunkErrorBoundary from '@/components/ChunkErrorBoundary';

// Suspense wrapper with chunk error recovery
const Lazy = ({ children }) => (
  <ChunkErrorBoundary>
    <Suspense fallback={<PageLoader />}>{children}</Suspense>
  </ChunkErrorBoundary>
);

// Protected Route Component
/** iter382: /login?next=<where you were heading>, same-site paths only. */
const loginWithReturn = (location) => {
  const target = `${location?.pathname || ''}${location?.search || ''}`;
  if (!target || target === '/' || target.startsWith('/login')) return '/login';
  return `/login?next=${encodeURIComponent(target)}`;
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (location.state?.user) return children;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to={loginWithReturn(location)} replace />;
  return children;
};

// Owner/Admin-only Route. Workers, bookkeepers and managers should never see
// billing / subscription pages — only the account owner (and platform admin)
// is responsible for payment. Deep-linking to /settings/billing as anyone
// else redirects to the dashboard so the app never surfaces "Vanskil" or
// "Klárðu áskrift" reminders to employees who cannot act on them.
const OwnerAdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  if (location.state?.user) return children;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to={loginWithReturn(location)} replace />;
  const role = user?.role;
  if (role !== 'owner' && role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    // iter382: an email link that bounced through the login keeps its
    // destination, otherwise this redirect would swallow it.
    const next = new URLSearchParams(location.search).get('next') || '';
    if (next.startsWith('/') && !next.startsWith('//')) {
      return <Navigate to={next} replace />;
    }
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (storedUser?.role === 'superadmin') return <Navigate to="/platform" replace />;
    // A signup that just succeeded goes straight to the welcome screen: the
    // user context flips while <Register> is still mounted, so without this
    // the dashboard painted for a frame before onboarding took over (iter366).
    let justRegistered = false;
    try { justRegistered = sessionStorage.getItem('rektra_just_registered') === '1'; } catch { /* private mode */ }
    if (justRegistered) return <Navigate to="/welcome" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
// PWA entry: authenticated → dashboard, otherwise → login
const PwaEntry = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
};

const AppRouter = () => {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/privacy" element={<Lazy><PrivacyPolicy /></Lazy>} />
      <Route path="/terms" element={<Lazy><TermsOfService /></Lazy>} />
      {/* /refunds removed (iter322): the 14-day refund promise contradicted the
          14-day free trial. Cancellation terms now live in Skilmálar only. */}
      <Route path="/refunds" element={<Navigate to="/terms" replace />} />
      <Route path="/refund" element={<Navigate to="/terms" replace />} />
      <Route path="/contact" element={<Lazy><ContactPage /></Lazy>} />
      <Route path="/pricing" element={<Lazy><Pricing /></Lazy>} />
      <Route path="/verd" element={<Lazy><VerdPricing /></Lazy>} />
      
      {/* Platform (Superadmin) — all routes share PlatformShell which gives
          a sticky tabbar + unified admin actions menu. */}
      <Route path="/platform" element={<ProtectedRoute><Lazy><PlatformShell /></Lazy></ProtectedRoute>}>
        <Route index element={<Lazy><PlatformDashboard /></Lazy>} />
        <Route path="companies/:id" element={<Lazy><PlatformCompanyDetail /></Lazy>} />
        <Route path="audit-logs" element={<Lazy><PlatformAuditLogs /></Lazy>} />
        <Route path="webhook-audit" element={<Lazy><PlatformWebhookAudit /></Lazy>} />
        <Route path="billing-reconciliation" element={<Lazy><PlatformBillingReconciliation /></Lazy>} />
        <Route path="billing-duplicates" element={<Lazy><PlatformBillingDuplicates /></Lazy>} />
        <Route path="sync-health" element={<Lazy><PlatformSyncHealth /></Lazy>} />
        <Route path="feature-inventory" element={<Lazy><PlatformFeatureInventory /></Lazy>} />
        <Route path="spine-test-walkthrough" element={<Lazy><PlatformSpineTestWalkthrough /></Lazy>} />
        <Route path="spine-test-bugs" element={<Lazy><PlatformSpineTestBugs /></Lazy>} />
        <Route path="spine-test-log" element={<Lazy><PlatformSpineTestLog /></Lazy>} />
        <Route path="invite-codes" element={<Lazy><PlatformInviteCodes /></Lazy>} />
        <Route path="feedback" element={<Lazy><PlatformFeedback /></Lazy>} />
        <Route path="waitlist" element={<Lazy><PlatformWaitlist /></Lazy>} />
      </Route>
      
      {/* PWA entry point - skips landing page */}
      <Route path="/app" element={<PwaEntry />} />

      {/* Landing page - public root */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/quote/:quoteId/respond" element={<Lazy><QuotePublic /></Lazy>} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
      
      {/* Lazy-loaded app pages */}
      <Route path="/time" element={<ProtectedRoute><Layout><Lazy><TimeTracking /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/receipts" element={<ProtectedRoute><Layout><Lazy><ReceiptsList /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/receipts/new" element={<ProtectedRoute><Layout><Lazy><ReceiptUpload /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/receipts/:receiptId" element={<ProtectedRoute><Layout><Lazy><ReceiptDetail /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><Layout><Lazy><Projects /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/projects/example" element={<ProtectedRoute><Lazy><ExampleProject /></Lazy></ProtectedRoute>} />
      <Route path="/projects/:projectId/aukavinna" element={<ProtectedRoute><Layout><Lazy><ProjectAukavinna /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><Layout><Lazy><ProjectDetail /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Layout><Lazy><Reports /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/reports/hours" element={<ProtectedRoute><Layout><Lazy><HoursOverview /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/financial-overview" element={<ProtectedRoute><Layout><Lazy><FinancialOverview /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/unit-prices" element={<ProtectedRoute><Layout><Lazy><UnitPrices /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/quotes" element={<ProtectedRoute><Layout><Lazy><Quotes /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/tilbodsgerd" element={<ProtectedRoute><Layout><Lazy><Tilbodsgerd /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/efnismagntaka" element={<ProtectedRoute><Layout><Lazy><Tilbodsgerd /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Layout><Lazy><Customers /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/customers/:customerId" element={<ProtectedRoute><Layout><Lazy><CustomerDetail /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/quotes/new" element={<ProtectedRoute><Layout><Lazy><QuoteEditor /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/quotes/:quoteId/edit" element={<ProtectedRoute><Layout><Lazy><QuoteEditor /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Layout><Lazy><CompanySettings /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/settings/suppliers" element={<ProtectedRoute><Layout><Lazy><SupplierOverrides /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/settings/accounting" element={<ProtectedRoute><BetaGate need="integrations"><Layout><Lazy><AccountingSettings /></Lazy></Layout></BetaGate></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><Layout><Lazy><AccountSettingsPage /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/instructions" element={<ProtectedRoute><Layout><Lazy><Instructions /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/settings/billing" element={<OwnerAdminRoute><BetaGate need="billing"><Layout><Lazy><BillingKontoSettings /></Lazy></Layout></BetaGate></OwnerAdminRoute>} />
      <Route path="/settings/billing-konto" element={<Navigate to="/settings/billing" replace />}/>
      <Route path="/settings/integrations" element={<ProtectedRoute><BetaGate need="integrations"><Layout><Lazy><IntegrationsSettings /></Lazy></Layout></BetaGate></ProtectedRoute>} />
      <Route path="/billing" element={<OwnerAdminRoute><BetaGate need="billing"><Layout><Lazy><BillingDashboard /></Lazy></Layout></BetaGate></OwnerAdminRoute>} />
      <Route path="/paywall" element={<OwnerAdminRoute><BetaGate need="billing"><Lazy><PaywallPage /></Lazy></BetaGate></OwnerAdminRoute>} />
      <Route path="/feedback" element={<ProtectedRoute><Layout><Lazy><Feedback /></Lazy></Layout></ProtectedRoute>} />
      
      {/* Standalone Categories page */}
      <Route path="/categories" element={<ProtectedRoute><Layout><Lazy><Categories /></Lazy></Layout></ProtectedRoute>} />
      <Route path="/locks" element={<Navigate to="/reports" replace />} />
      <Route path="/exports" element={<Navigate to="/reports" replace />} />
      <Route path="/users" element={<Navigate to="/settings" replace />} />
      
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// Syncs the user's language preference from the backend to I18nContext
const LangSync = () => {
  const { user } = useAuth();
  const { initLang } = useI18n();
  React.useEffect(() => {
    if (user?.language) initLang(user.language);
  }, [user?.language, initLang]);
  return null;
};

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <PlanProvider>
        <PaywallProvider>
        <BrowserRouter>
          <LangSync />
          <ImpersonationBanner />
          <AppRouter />
          <ConfirmDialogHost />
          <Toaster position="top-center" richColors closeButton visibleToasts={1} toastOptions={{ style: { marginTop: '3.5rem' } }} />
        </BrowserRouter>
          </PaywallProvider>
      </PlanProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
